import type { Unidade } from 'types/pessoa';
import type { DirecionadorTransversal } from 'utils/direcionadores';
import { api } from './api';
import { dedupedFetch, invalidateCache } from './cache';

/** Pessoa elegível como participante, só com os campos de exibição. */
export interface PessoaParticipante {
  _id: string;
  nome: string;
  nome_guerra?: string | null;
  funcao?: { nome?: string } | null;
  lotacao?: { prefixo?: number; nome?: string } | null;
}

const ENTREGAS_PRE_INSCRICAO_CACHE_KEY = 'entregas-pre-inscricao';

/** Direcionadores transversais ativos, na ordem de exibição (aggregate `direcionadores_transversais`). */
export function buscarDirecionadores(): Promise<DirecionadorTransversal[]> {
  return dedupedFetch('direcionadores-transversais', async () => {
    const { data } = await api.post('/find/direcionadores_transversais', {});
    return Array.isArray(data) ? (data as DirecionadorTransversal[]) : [];
  }, 10 * 60_000);
}

/** UT/UE que podem validar a pré-inscrição (aggregate `unidades_validadoras`). */
export function buscarUnidadesValidadoras(): Promise<Unidade[]> {
  return dedupedFetch('unidades-validadoras', async () => {
    const { data } = await api.post('/find/unidades_validadoras', {});
    return Array.isArray(data) ? (data as Unidade[]) : [];
  }, 10 * 60_000);
}

/**
 * Funcionários elegíveis por matrícula ou parte do nome (mín. 3 caracteres).
 * O servidor aplica o filtro de inelegíveis e, quando informado, o de programa.
 */
export async function buscarParticipantes(termo: string, programa?: string | null): Promise<PessoaParticipante[]> {
  const { data } = await api.post('/find/pessoas_participantes', {
    termo,
    ...(programa ? { programa } : {}),
  });
  return Array.isArray(data) ? (data as PessoaParticipante[]) : [];
}

/** Pré-inscrições em que o usuário logado é dono ou participante (o servidor usa o player do token). */
export function buscarEntregasPreInscricao(): Promise<any[]> {
  return dedupedFetch(ENTREGAS_PRE_INSCRICAO_CACHE_KEY, async () => {
    const { data } = await api.post('/find/entregas_pre_inscricao', {});
    return Array.isArray(data) ? data : [];
  }, 30_000);
}

export function invalidarEntregasPreInscricao(): void {
  invalidateCache(ENTREGAS_PRE_INSCRICAO_CACHE_KEY);
}

/** Iniciativas do próprio usuário para decidir a tela de entrada; falha vira lista vazia. */
export async function buscarIniciativasDoUsuario(): Promise<any[]> {
  try {
    return await buscarEntregasPreInscricao();
  } catch (error) {
    console.error('[buscarIniciativasDoUsuario] Erro ao buscar iniciativas:', error);
    return [];
  }
}
